import 'overlayscrollbars/overlayscrollbars.css';

import {
  type FC,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
} from 'react';
import ReactModal from 'react-modal';

import {Observer} from 'mobx-react';

import type {ModalRef, OnRequestCloseEvent, Props} from './types';

import {useInjection} from '../../IoC';
import {ModalBody} from './components/ModalBody/ModalBody';
import {ModalFooter} from './components/ModalFooter/ModalFooter';
import {ModalHeader} from './components/ModalHeader/ModalHeader';
import {ModalLoading} from './components/ModalLoading/ModalLoading';
import {CLOSE_TIMEOUT_MS} from './constants';
import {ModalController} from './stores/ModalController/Modal.controller';
import * as S from './styles';

export const Modal: FC<Props> = forwardRef<ModalRef, Props>(
  (
    {
      afterTitleSlot,
      children,
      className = '',
      isClosable,
      isFullWidth,
      isLoading = false,
      isOpen: isOpenProp = false,
      maxHeight,
      maxWidth,
      minHeight,
      minWidth,
      modalOffset,
      modalPadding,
      onAfterOpen,
      onBeforeClose,
      onRequestClose,
      overlayZIndex,
      renderFooter,
      title,
      ...rest
    },
    ref,
  ) => {
    const controller = useInjection(ModalController);

    useEffect(() => {
      controller.init(
        {
          isClosable,
          isFullWidth,
          maxHeight,
          maxWidth,
          minHeight,
          minWidth,
          modalOffset,
          modalPadding,
          overlayZIndex,
        },
        isOpenProp,
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controller]);

    useImperativeHandle(ref, () => ({
      hide: () => {
        controller.isRefUsed.setFalse();

        controller.hide();
      },
      show: () => {
        controller.isRefUsed.setTrue();

        controller.show();
      },
    }));

    useEffect(() => {
      controller.handleRefOpenProp(isOpenProp);
      controller.isLoading.setValue(isLoading);
    }, [controller, isOpenProp, isLoading]);

    const onAfterOpenCb = useCallback(() => {
      controller.resize.observe();

      onAfterOpen?.();
    }, [controller, onAfterOpen]);

    const onRequestCloseCb = useCallback(
      async (e: OnRequestCloseEvent) => {
        let shouldClose = true;

        if (onBeforeClose) {
          shouldClose = await onBeforeClose();
        }

        if (!shouldClose) {
          return;
        }

        controller.hide();
        onRequestClose?.(e);
      },
      [controller, onRequestClose, onBeforeClose],
    );

    return (
      <>
        <Observer>
          {() => (
            <S.ModalGlobalStyles
              config={controller.config}
              id={controller.id}
            />
          )}
        </Observer>
        <Observer>
          {() => (
            <ReactModal
              className={controller.selectors.buildReactModalClassName(
                className,
              )}
              closeTimeoutMS={CLOSE_TIMEOUT_MS}
              isOpen={controller.isOpen.value}
              onAfterOpen={onAfterOpenCb}
              onRequestClose={onRequestCloseCb}
              overlayClassName={controller.selectors.overlayClassName}
              parentSelector={() => controller.nodes.getParent()}
              shouldCloseOnEsc={controller.isAbleToClose}
              shouldCloseOnOverlayClick={controller.isAbleToClose}
              {...rest}
            >
              <ModalHeader
                afterTitleSlot={afterTitleSlot}
                isClosable={controller.config.isClosable}
                modalPadding={controller.config.modalPadding}
                onRequestClose={onRequestCloseCb}
                renderCloseIcon={controller.config.renderCloseIcon}
                title={title}
                wrapperId={controller.selectors.headerWrapperId}
              />
              <ModalBody
                modalPadding={controller.config.modalPadding}
                scrollContainerId={controller.selectors.scrollContainerId}
              >
                {children}
              </ModalBody>
              <ModalFooter
                footerContainerId={controller.selectors.footerContainerId}
                modalPadding={controller.config.modalPadding}
                renderFooter={renderFooter}
              />
              <ModalLoading isLoading={controller.isLoading.value} />
            </ReactModal>
          )}
        </Observer>
      </>
    );
  },
);
